/**
 * <buy-campaign-look>
 *
 * Renders a small horizontal product feed (max 6) from the `campaign_products`
 * URL parameter, which holds comma-separated product handles:
 *   ?campaign_products=handle-a,handle-b,handle-c
 *
 * Each handle is resolved client-side by fetching a fully-rendered product card
 * from sections/buy-campaign-look-card.liquid via the Section Rendering API:
 *   {root}/products/{handle}?section_id=buy-campaign-look-card
 * Because the request hits a product URL, Liquid binds the `product` object to
 * that handle's product, so the returned card is identical to the collection
 * grid (card-product snippet) with market/locale-correct pricing and badges.
 *
 * The element stays hidden until at least one card resolves, so there is no
 * layout shift when the parameter is absent or all handles are invalid.
 *
 * Reads from data attributes set by sections/buy-campaign-look.liquid:
 *   data-param        — URL parameter name (default "campaign_products")
 *   data-max          — max products to show (default 6)
 *   data-root         — localized root path for the fetch (e.g. "/" or "/en/")
 *   data-card-section — section id of the single-card renderer
 */
class BuyCampaignLook extends HTMLElement {
  constructor() {
    super();
    this.paramName = this.dataset.param || 'campaign_products';
    this.maxProducts = parseInt(this.dataset.max, 10) || 6;
    this.root = this.dataset.root || '/';
    this.cardSection = this.dataset.cardSection || 'buy-campaign-look-card';
    this.feed = this.querySelector('[data-feed]');
  }

  connectedCallback() {
    const handles = this.getHandles();
    if (!handles.length || !this.feed) return; // stays hidden
    this.render(handles);
  }

  getHandles() {
    const raw = new URLSearchParams(window.location.search).get(this.paramName);
    if (!raw) return [];

    const seen = new Set();
    const handles = [];
    raw.split(',').forEach((token) => {
      const handle = token.trim().toLowerCase();
      if (handle && !seen.has(handle)) {
        seen.add(handle); // de-dupe
        handles.push(handle);
      }
    });
    return handles.slice(0, this.maxProducts);
  }

  async render(handles) {
    const base = this.root.replace(/\/$/, '');
    const results = await Promise.allSettled(
      handles.map((handle) =>
        fetch(
          `${base}/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(this.cardSection)}`
        ).then((response) => {
          if (!response.ok) throw new Error(`Campaign product not found: ${handle}`);
          return response.text();
        })
      )
    );

    // Keep URL order, drop unresolved (404) handles, parse the rendered card.
    const cards = results
      .filter((result) => result.status === 'fulfilled')
      .map((result) => this.parseCard(result.value))
      .filter(Boolean);

    if (!cards.length) return; // stays hidden

    const fragment = document.createDocumentFragment();
    cards.forEach((card) => fragment.appendChild(card));
    this.feed.appendChild(fragment);

    this.hidden = false;
    this.classList.add('buy-campaign-look--loaded');
  }

  // Extract the rendered <li.buy-campaign-look__item> from a Section Rendering
  // API response (the card is wrapped in Shopify's <div id="shopify-section-…">).
  parseCard(html) {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    return wrapper.querySelector('.buy-campaign-look__item');
  }
}

customElements.define('buy-campaign-look', BuyCampaignLook);
